import { scrollToSection } from './scrollToSection';

// Where a field jump came from, so the page can offer a way back to that drawer.
export type JumpOrigin = { kind: 'ai'; tab: string } | { kind: 'suggestions' };

// Map an AI field path (the backend's bracket-path shape, e.g. "victims[<id>].gender",
// "violationDetails[<id>].accusations", "country") to the page section(s) that show it, most
// specific first. The caller tries each candidate until one exists on the page.
const TOP_LEVEL_SECTION: Record<string, string> = {
  description: 'section-incident', caseStartDate: 'section-incident', caseEndDate: 'section-incident',
  incidentTime: 'section-incident', isCaseOngoing: 'section-incident', reportedDate: 'section-incident',
  country: 'section-location', region: 'section-location', city: 'section-location',
  specificLocation: 'section-location', latitude: 'section-location', longitude: 'section-location',
  indigenousGroup: 'section-indigenous-group', indigenousTerritory: 'section-indigenous-group',
  victims: 'section-victims',
  perpetrators: 'section-perpetrators',
  familyImpact: 'section-impact', communityImpact: 'section-impact', impactTypes: 'section-impact',
  isRelatedToDevelopmentProject: 'section-development-project', developmentProjects: 'section-development-project',
  // Legacy flat paths (records and ledger entries from before the project table).
  developmentProjectType: 'section-development-project',
  developmentProjectName: 'section-development-project', developmentProjectDescription: 'section-development-project',
  developmentProjectStartYear: 'section-development-project', developmentProjectSource: 'section-development-project',
  developmentProjectWebsiteLink: 'section-development-project',
  investigationConducted: 'section-investigations', investigationDetails: 'section-investigations',
  investigationWhyNot: 'section-investigations',
  advocacyUndertaken: 'section-advocacy', advocacyDetails: 'section-advocacy', advocacyWhyNot: 'section-advocacy',
  documentorName: 'section-data-source', documentorOrganization: 'section-data-source',
  documentorEmail: 'section-data-source', documentorPhone: 'section-data-source',
  dataSource: 'section-data-source', dataUsageRestriction: 'section-data-source',
  isPrimaryData: 'section-data-source', dataBasis: 'section-data-source', sourceInformation: 'section-data-source', additionalSources: 'section-data-source',
};

const VIOLATION_SECTION: Record<string, string> = {
  CRIMINALIZATION: 'section-criminalization',
  KILLING: 'section-killing',
  OTHER: 'section-other-violations',
};
const ALL_VIOLATION_SECTIONS = ['section-criminalization', 'section-killing', 'section-other-violations'];

export function fieldAnchorCandidates(path: string, caseData?: Record<string, any> | null): string[] {
  const segments = path.replace(/\[([^\]]+)\]/g, '.$1').split('.');
  const [root, second] = segments;

  if (root === 'victims') {
    return second ? [`victim-${second}`, 'section-victims'] : ['section-victims'];
  }
  if (root === 'perpetrators') {
    return second ? [`perpetrator-${second}`, 'section-perpetrators'] : ['section-perpetrators'];
  }
  if (root === 'developmentProjects') {
    return second ? [`development-project-${second}`, 'section-development-project'] : ['section-development-project'];
  }
  if (root === 'violationDetails') {
    const detail = second && Array.isArray(caseData?.violationDetails)
      ? (caseData!.violationDetails as any[]).find((d) => d?.id === second)
      : null;
    const own = detail?.violationType ? VIOLATION_SECTION[detail.violationType] : null;
    return own ? [own, ...ALL_VIOLATION_SECTIONS.filter((s) => s !== own)] : [...ALL_VIOLATION_SECTIONS];
  }
  if (root === 'violationTypes' || root === 'otherViolationTypes' || root === 'otherViolationTypeDetails') {
    return [...ALL_VIOLATION_SECTIONS, 'section-victims'];
  }
  const section = TOP_LEVEL_SECTION[root];
  return section ? [section] : [];
}

// Scroll to the first candidate section that is on the page, asking a collapsed card to open
// first. Returns false when nothing matched.
export function jumpToField(path: string, caseData: Record<string, any> | null | undefined, offset = 0): boolean {
  for (const id of fieldAnchorCandidates(path, caseData)) {
    if (!document.getElementById(id)) continue;
    window.dispatchEvent(new CustomEvent('ipri:expand-section', { detail: id }));
    return scrollToSection(id, offset, { flash: true });
  }
  return false;
}
